import { memo, type Ref } from 'react';

import { isS3RecordingUrl } from '@apps/web/lib/contact_utils';
import { type VoiceCall } from '@libraries/typescript/api_client';

type VoiceCallAudioPlayerProps = {
    readonly voiceCall: Pick<VoiceCall, 'id' | 'recording_url'> | null;
    readonly className?: string;
    readonly prefetchedRecordingUrl?: string;
    readonly isRecordingUrlLoading?: boolean;
    readonly recordingUrlError?: unknown;
    // Optional handle to the underlying audio element so callers (e.g. the
    // Voice Calls transcript) can seek playback to a turn's offset. Additive:
    // existing callers pass no ref and are unaffected.
    readonly audioRef?: Ref<HTMLAudioElement>;
};

/**
 * A reusable audio player component for voice call recordings.
 *
 * Works for call recordings in S3 and legacy historical Retell call recordings.
 */
export const VoiceCallAudioPlayer = memo(
    ({
        voiceCall,
        className = 'h-8 max-w-[200px] focus:outline-none',
        prefetchedRecordingUrl,
        isRecordingUrlLoading = false,
        recordingUrlError,
        audioRef,
    }: VoiceCallAudioPlayerProps) => {
        if (prefetchedRecordingUrl) {
            return (
                <audio
                    ref={audioRef}
                    aria-label="Call recording"
                    controls={true}
                    src={prefetchedRecordingUrl}
                    className={className}
                >
                    Your browser does not support the audio element.
                </audio>
            );
        }

        if (!voiceCall) {
            return null;
        }

        const rawRecordingUrl = voiceCall.recording_url;

        if (!rawRecordingUrl || typeof rawRecordingUrl !== 'string') {
            return null;
        }

        // If it's an S3 URL, use the signed URL endpoint. Otherwise, it is a
        // public Cloudfront URL from Retell which we can use directly.
        const isS3Url = isS3RecordingUrl(rawRecordingUrl);

        // If it is an S3 URL, a signed version will be provided eventually for
        // prefetchedRecordingUrl. Legacy public URLs can be used directly,
        // including from admin pages without a selected organization.
        const recordingUrl = isS3Url ? null : rawRecordingUrl;

        if (!recordingUrl) {
            const message = isRecordingUrlLoading
                ? 'Recording playback is still being prepared.'
                : recordingUrlError
                  ? 'Recording playback URL could not be loaded.'
                  : 'Recording is stored, but playback is not available yet.';

            return (
                <p className="text-muted-foreground text-sm" role="status">
                    {message}
                </p>
            );
        }

        return (
            <audio
                ref={audioRef}
                aria-label="Call recording"
                controls={true}
                src={recordingUrl}
                className={className}
            >
                Your browser does not support the audio element.
            </audio>
        );
    },
    (prevProps, nextProps) => {
        return (
            prevProps.voiceCall?.id === nextProps.voiceCall?.id &&
            prevProps.prefetchedRecordingUrl ===
                nextProps.prefetchedRecordingUrl &&
            prevProps.isRecordingUrlLoading ===
                nextProps.isRecordingUrlLoading &&
            prevProps.recordingUrlError === nextProps.recordingUrlError &&
            prevProps.audioRef === nextProps.audioRef
        );
    },
);

VoiceCallAudioPlayer.displayName = 'VoiceCallAudioPlayer';
